/**
 * Shrinking a photo before it leaves the phone.
 *
 * **Resized in the browser, not on the server**, and the reasons compound:
 *
 *  - A phone camera produces 4–12 MB. Workshop wifi is the worst network this
 *    app runs on, and it is the one every teardown photo is taken on.
 *  - Resizing server-side means `sharp` — a native dependency in the production
 *    image, on a 2 GB droplet shared with Cadence, to do work the device that
 *    took the photo can do for free while the person is still looking at it.
 *  - The browser knows the dimensions it produced. The server would need the
 *    image library just to learn two integers used only for layout.
 *
 * One size, not a thumbnail pair. 1400px is small enough to serve into a 64px
 * card and large enough to read a part number off a teardown shot, and because
 * the store is content-addressed a list of eight types is eight images the
 * browser then caches forever. A second size would double the store and the
 * upload for a saving that only appears on a list nobody scrolls.
 */

export const MAX_EDGE = 1400
/** JPEG at this quality is visually clean and roughly a fifth of the bytes of
 *  0.95. Above ~0.85 the file grows much faster than anything you can see. */
const QUALITY = 0.82

export interface PreparedImage {
  readonly blob: Blob
  readonly width: number
  readonly height: number
  /** For an instant preview, before the upload has even started. */
  readonly previewUrl: string
}

export type PrepareResult =
  | { readonly ok: true; readonly image: PreparedImage }
  | { readonly ok: false; readonly reason: string }

export async function prepareImage(file: File): Promise<PrepareResult> {
  if (!file.type.startsWith('image/')) {
    return { ok: false, reason: `${file.name} is not an image` }
  }

  let bitmap: ImageBitmap
  try {
    // `createImageBitmap` decodes off the main thread, so a 12 MP photo does
    // not freeze the page on a mid-range phone — which is every phone here.
    bitmap = await createImageBitmap(file)
  } catch {
    // HEIC is the common case: iOS hands it over labelled as an image and no
    // browser outside Safari can decode it. Say the useful thing rather than
    // "failed to load".
    return {
      ok: false,
      reason:
        'that image could not be read — if it came from an iPhone, set Camera → Formats → Most Compatible, or take the photo from this page',
    }
  }

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
  const width = Math.max(1, Math.round(bitmap.width * scale))
  const height = Math.max(1, Math.round(bitmap.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) return { ok: false, reason: 'this browser cannot resize images' }
  context.drawImage(bitmap, 0, 0, width, height)
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) => {
    // Always JPEG. A screenshot pasted in as a PNG of a photograph is several
    // times the size for no gain, and the store does not care what it holds.
    canvas.toBlob(resolve, 'image/jpeg', QUALITY)
  })
  if (!blob) return { ok: false, reason: 'this browser could not encode the image' }

  return { ok: true, image: { blob, width, height, previewUrl: URL.createObjectURL(blob) } }
}

/** Where a photo's bytes live. Immutable, so the browser caches it forever. */
export function imageUrl(attachmentId: string): string {
  return `/api/attachments/${encodeURIComponent(attachmentId)}/bytes`
}
